<template>
  <div>
    <!-- Row: Units / Users -->
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="6" class="mb-2">
        <label>REMS</label>
        <b-form-input
          :value="remsFilter"
          type="number"
          min="0"
          class="w-100"
          :placeholder="$t('id') || 'ID'"
          @input="(v) => $emit('update:remsFilter', v !== '' && v != null ? Number(v) : null)"
        />
      </b-col>
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $t("users.title") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="userFilter"
          :options="userOptions"
          class="w-100"
          multiple
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:userFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
    </b-row>

    <!-- Row: REMS / SIRH (IDs) -->
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="6" class="mb-2">
        <label>SIRH</label>
        <b-form-input
          :value="sirhFilter"
          type="number"
          min="0"
          class="w-100"
          :placeholder="$t('id') || 'ID'"
          @input="(v) => $emit('update:sirhFilter', v !== '' && v != null ? Number(v) : null)"
        />
      </b-col>
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $t("country.title") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="countryFilter"
          :options="countryOptions"
          class="w-100"
          multiple
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:countryFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
    </b-row>

    <!-- Row: Countries / Municipalities -->
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $t("municipality.title") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="municipalityFilter"
          :options="municipalityOptions"
          class="w-100"
          multiple
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:municipalityFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $t("risk.filters.status_form") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="statusFilter"
          :options="localStatusOptions"
          class="w-100"
          multiple
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:statusFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
    </b-row>

    <!-- Row: Status / Responsable -->
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $t("risk.status_assigned") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="responsableFilter"
          :options="userOptions"
          class="w-100"
          multiple
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:responsableFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $t("risk.status_in_review") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="assignedFilter"
          :options="userOptions"
          class="w-100"
          multiple
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:assignedFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
    </b-row>


    <!-- Row: Date range -->
    <b-row>
      <b-col cols="6" class="mb-0">
        <label class="text-capitalize">{{ $t("log_microservice.start_date") }}</label>
        <div class="input-group">
          <flat-pickr
            :value="startDate"
            class="form-control"
            @input="(val) => dateStartClick(val)"
            :config="pickerCfg"
            :style="dateStartRequired ? 'border: solid red 1px' : ''"
          />
          <div class="input-group-append">
            <button @click="handleClearDates()" class="btn btn-outline-primary" type="button">x</button>
          </div>
        </div>
        <div class="errorText" v-show="dateStartRequired">
          {{ $t("log_microservice.The_start_date_value_is_required_for_filter_by_date") }}
        </div>
      </b-col>
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $t("log_microservice.end_date") }}</label>
        <div class="input-group">
          <flat-pickr
            :value="endDate"
            class="form-control"
            @input="(val) => dateEndClick(val)"
            :config="pickerCfg"
            :style="dateEndRequired ? 'border: solid red 1px' : ''"
          />
          <div class="input-group-append">
            <button @click="handleClearDates()" class="btn btn-outline-primary" type="button">x</button>
          </div>
        </div>
        <div class="errorText" v-show="dateEndRequired">
          {{ $t("log_microservice.The_end_date_value_is_required_to_filter_by_date") }}
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BRow, BCol, BFormInput } from "bootstrap-vue";
import vSelect from "vue-select";
import popperHelper from "@/libs/popper-helper";
import flatPickr from "vue-flatpickr-component";

export default {
  name: "FiltersSecuritySelfAudits",
  components: {
    BRow,
    BCol,
    BFormInput,
    vSelect,
    flatPickr,
  },
  props: {
    unitOptions: { type: Array, default: () => [] },
    unitFilter: { type: Array, default: () => [] },

    userOptions: { type: Array, default: () => [] },
    userFilter: { type: Array, default: () => [] },

    countryOptions: { type: Array, default: () => [] },
    countryFilter: { type: Array, default: () => [] },

    municipalityOptions: { type: Array, default: () => [] },
    municipalityFilter: { type: Array, default: () => [] },

    // Allow parent to override status options
    statusOptions: {
      type: Array,
      default: () => [],
    },
    statusFilter: {
      type: Array,
      default: () => [],
    },
    responsableFilter: {
      type: Array,
      default: () => [],
    },
    assignedFilter: {
      type: Array,
      default: () => [],
    },

    startDate: { type: String, default: null },
    endDate: { type: String, default: null },
    remsFilter: { type: [Number, String, null], default: null },
    sirhFilter: { type: [Number, String, null], default: null },
  },
  data() {
    return {
      dateStartRequired: false,
      dateEndRequired: false,
      dateStartComponent: null,
      dateEndComponent: null,
      pickerCfg: {
        maxDate: new Date(),
        enableTime: true,
        dateFormat: "Y-m-d H:i",
        time_24hr: true,
        minuteIncrement: 1,
      },
    };
  },
  computed: {
    // Fallback to SSA status options if parent didn't provide any
    localStatusOptions() {
      if (Array.isArray(this.statusOptions) && this.statusOptions.length) {
        return this.statusOptions;
      }
      // Default SSA states (single select)
      return [
        { label: this.$t("security_self_audit.states.created") || "Created", value: "created" },
        { label: this.$t("security_self_audit.states.assigned") || "Assigned", value: "assigned" },
        { label: this.$t("security_self_audit.states.reasigned") || "Reassigned", value: "reasigned" }, // catalog typo support
        { label: this.$t("security_self_audit.states.in_review") || "In review", value: "in_review" },
        { label: this.$t("security_self_audit.states.approved") || "Approved", value: "approved" },
        { label: this.$t("security_self_audit.states.cancelled") || "Cancelled", value: "cancelled" },
        { label: this.$t("security_self_audit.states.not_approved") || "Not approved", value: "not_approved" },
      ];
    },
  },
  methods: {
    // Validate that both dates are present to apply date filter
    verification() {
      const start = this.dateStartComponent ?? this.startDate;
      const end = this.dateEndComponent ?? this.endDate;

      this.dateStartRequired = !start;
      this.dateEndRequired = !end;

      if (start && end) {
        this.$emit("update:startDate", this.dateStartComponent || this.startDate);
        this.$emit("update:endDate", this.dateEndComponent || this.endDate);
      }
    },
    // Clear both dates at once
    handleClearDates() {
      this.$emit("update:startDate", null);
      this.dateStartRequired = false;
      this.dateStartComponent = null;
      this.$emit("update:endDate", null);
      this.dateEndRequired = false;
      this.dateEndComponent = null;
    },
    // Set start date and verify pair
    dateStartClick(val) {
      if (val !== "") {
        this.dateStartComponent = val;
        this.verification();
      }
    },
    // Set end date and verify pair
    dateEndClick(val) {
      if (val !== "") {
        this.dateEndComponent = val;
        this.verification();
      }
    },
  },
  setup() {
    return { withPopper: popperHelper.withPopper };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.errorText {
  color: red;
  font-size: 12px;
}
</style>
